import {useMemo, useState} from 'react'
import {Button, Spinner, Form, Row, Col, Badge} from 'react-bootstrap'
import {useQuery, useMutation} from '@apollo/client'
import {toast} from 'react-toastify'
import Select from 'react-select'
import {BULK_ASSIGN_PERMISSIONS, PERMISSION_USERS} from '../../../../gql/authz'
import {parseId} from '../../../../helpers'

export type Perm = {codename: string; label: string; description: string}
export type PermModule = {key: string; title: string; permissions: Perm[]}
export type Target = {id: string; name: string; member_count: number; permissions: string[]}

type TargetType = 'role' | 'group' | 'user'

// Bulk assignment: pick permissions (by module), then apply to many roles /
// groups / users at once.
export const BulkAssignPanel = ({
  modules,
  roles,
  groups,
  onDone,
}: {
  modules: PermModule[]
  roles: Target[]
  groups: Target[]
  onDone: () => void
}) => {
  const [bulkAssign, {loading: applying}] = useMutation(BULK_ASSIGN_PERMISSIONS)
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const [targetType, setTargetType] = useState<TargetType>('role')
  const [checkedTargets, setCheckedTargets] = useState<Set<string>>(new Set())
  const [userTargets, setUserTargets] = useState<{value: string; label: string}[]>([])
  const [effect, setEffect] = useState<'allow' | 'deny' | 'clear'>('allow')
  const [userSearch, setUserSearch] = useState('')

  const {data: usersData} = useQuery(PERMISSION_USERS, {
    variables: {first: 50, isActive: true, name: userSearch || null},
    skip: targetType !== 'user',
    fetchPolicy: 'cache-and-network',
  })
  const userOptions = useMemo(
    () =>
      (usersData?.allUsers?.edges ?? []).map((e: any) => {
        const name = `${e.node.firstName || ''} ${e.node.lastName || ''}`.trim()
        return {
          value: String(parseId(e.node.id)),
          label: name ? `${name} — ${e.node.email}` : e.node.email,
        }
      }),
    [usersData]
  )

  const togglePerm = (cn: string) =>
    setPicked((prev) => {
      const n = new Set(prev)
      n.has(cn) ? n.delete(cn) : n.add(cn)
      return n
    })
  const toggleModulePerms = (mod: PermModule, on: boolean) =>
    setPicked((prev) => {
      const n = new Set(prev)
      mod.permissions.forEach((p) => (on ? n.add(p.codename) : n.delete(p.codename)))
      return n
    })
  const toggleTarget = (id: string) =>
    setCheckedTargets((prev) => {
      const n = new Set(prev)
      n.has(id) ? n.delete(id) : n.add(id)
      return n
    })

  const targetIds =
    targetType === 'user' ? userTargets.map((u) => u.value) : Array.from(checkedTargets)

  // For roles/groups the effect is grant/revoke (allow/clear). For users it's allow/deny/clear.
  const effectOptions =
    targetType === 'user'
      ? [
          {v: 'allow', l: 'Allow (grant override)'},
          {v: 'deny', l: 'Deny (block override)'},
          {v: 'clear', l: 'Clear (inherit)'},
        ]
      : [
          {v: 'allow', l: 'Grant'},
          {v: 'clear', l: 'Revoke'},
        ]

  const setType = (t: TargetType) => {
    setTargetType(t)
    setCheckedTargets(new Set())
    setUserTargets([])
    if (t !== 'user' && effect === 'deny') setEffect('allow')
  }

  const canApply = picked.size > 0 && targetIds.length > 0 && !applying

  const handleApply = async () => {
    if (!canApply) return
    const noun = targetType === 'user' ? 'user(s)' : targetType === 'group' ? 'group(s)' : 'role(s)'
    const verb = effect === 'clear' ? 'Clear' : effect === 'deny' ? 'Deny' : targetType === 'user' ? 'Allow' : 'Grant'
    if (!window.confirm(`${verb} ${picked.size} permission(s) on ${targetIds.length} ${noun}?`)) return
    try {
      const {data} = await bulkAssign({
        variables: {codenames: Array.from(picked), effect, targetType, targetIds},
      })
      const res = data?.bulkAssignPermissions
      if (!res?.success) throw new Error('The permission assignment was not saved')
      toast.success(`Applied to ${res.updated} ${noun}.`)
      setPicked(new Set())
      setCheckedTargets(new Set())
      setUserTargets([])
      onDone()
    } catch (err: any) {
      toast.error(err?.message || 'Bulk assignment failed')
    }
  }

  return (
    <Row className='g-4'>
      {/* 1. Permissions */}
      <Col lg={7}>
        <div className='fw-bold mb-2'>1. Pick permissions <Badge bg='light' text='dark'>{picked.size}</Badge></div>
        <div className='border rounded p-3' style={{maxHeight: '60vh', overflow: 'auto'}}>
          {modules.map((mod) => {
            const all = mod.permissions.every((p) => picked.has(p.codename))
            return (
              <div key={mod.key} className='mb-3'>
                <Form.Check
                  type='checkbox'
                  id={`bulk-mod-${mod.key}`}
                  className='fw-bolder text-uppercase fs-8 text-gray-700'
                  label={mod.title}
                  checked={all}
                  onChange={(e) => toggleModulePerms(mod, e.target.checked)}
                />
                <div className='ms-4'>
                  {mod.permissions.map((p) => (
                    <Form.Check
                      key={p.codename}
                      type='checkbox'
                      id={`bulk-${p.codename}`}
                      label={<span>{p.label} <code className='text-muted fs-9'>{p.codename}</code></span>}
                      checked={picked.has(p.codename)}
                      onChange={() => togglePerm(p.codename)}
                    />
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      </Col>

      {/* 2. Targets + 3. Apply */}
      <Col lg={5}>
        <div className='fw-bold mb-2'>2. Apply to</div>
        <div className='btn-group btn-group-sm mb-3' role='group'>
          {(['role', 'group', 'user'] as TargetType[]).map((t) => (
            <button
              key={t}
              type='button'
              className={`btn btn-${targetType === t ? 'primary' : 'light'}`}
              onClick={() => setType(t)}
            >
              {t === 'role' ? 'Roles' : t === 'group' ? 'Groups' : 'Users'}
            </button>
          ))}
        </div>

        {targetType === 'user' ? (
          <Select
            isMulti
            options={userOptions}
            value={userTargets}
            onChange={(v: any) => setUserTargets(v || [])}
            onInputChange={(v: string) => setUserSearch(v)}
            filterOption={() => true}
            placeholder='Type to search users…'
            classNamePrefix='rs'
          />
        ) : (
          <div className='border rounded p-3' style={{maxHeight: '38vh', overflow: 'auto'}}>
            {(targetType === 'role' ? roles : groups).map((t) => (
              <Form.Check
                key={t.id}
                type='checkbox'
                id={`bulk-target-${targetType}-${t.id}`}
                label={<span>{t.name} <span className='text-muted fs-8'>({t.member_count})</span></span>}
                checked={checkedTargets.has(t.id)}
                onChange={() => toggleTarget(t.id)}
              />
            ))}
          </div>
        )}

        <div className='fw-bold mt-4 mb-2'>3. Effect</div>
        <Form.Select size='sm' value={effect} onChange={(e) => setEffect(e.target.value as any)}>
          {effectOptions.map((o) => (
            <option key={o.v} value={o.v}>{o.l}</option>
          ))}
        </Form.Select>

        <Button className='mt-4 w-100' variant='primary' onClick={handleApply} disabled={!canApply}>
          {applying ? <Spinner animation='border' size='sm' /> : `Apply to ${targetIds.length || 0} target(s)`}
        </Button>
        <div className='text-muted fs-8 mt-2'>
          {targetType !== 'user' && 'Grant adds the permission; Revoke removes it. '}
          {targetType === 'user' && 'Allow/Deny set a per-user override; Clear returns to inherited. '}
          Changes take effect on the next permission refresh.
        </div>
      </Col>
    </Row>
  )
}
